import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SearchInput,
  Skeleton,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@provenance/design';
import { UserRole } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Building2, Pencil, PenLine, Plus, Power, RefreshCw, Upload } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toBase64 } from '../features/configuration/upload';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';

interface ExporterRow {
  id: string;
  companyName: string;
  tradingName: string | null;
  abn: string;
  active: boolean;
  certificatesIssued: number;
  signatoryCount: number;
  currentSignatories: number;
  expiredSignatories: number;
  needsSpecimen: boolean;
}

type SignatoryStatus = 'CURRENT' | 'EXPIRING' | 'EXPIRED' | 'NO_SPECIMEN' | 'INACTIVE';

interface Signatory {
  id: string;
  fullName: string;
  position: string;
  email: string | null;
  specimenUrl: string | null;
  validFrom: string;
  validUntil: string | null;
  active: boolean;
  status: SignatoryStatus;
  daysUntilExpiry: number | null;
}

interface ExporterAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  active: boolean;
  hasPassword: boolean;
  lastLoginAt: string | null;
}

interface ExporterDetailData {
  id: string;
  companyName: string;
  tradingName: string | null;
  abn: string;
  active: boolean;
  addressLine1: string;
  addressLine2: string | null;
  suburb: string;
  state: string;
  postcode: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  documentCount: number;
  users: ExporterAccount[];
  validityMonths: number;
  signatories: Signatory[];
}

const STATUS: Record<SignatoryStatus, { label: string; tone: 'ok' | 'warn' | 'stop' | 'neutral' }> = {
  CURRENT: { label: 'Current', tone: 'ok' },
  EXPIRING: { label: 'Expiring soon', tone: 'warn' },
  EXPIRED: { label: 'Expired', tone: 'stop' },
  NO_SPECIMEN: { label: 'No specimen', tone: 'warn' },
  INACTIVE: { label: 'Inactive', tone: 'neutral' },
};

/**
 * Exporters and the specimen signatures a chamber holds for them.
 *
 * The chamber signs the certificate; the exporter signs the declaration behind it. A
 * verified specimen on file is what lets an operator check that the signature on a
 * declaration belongs to someone the exporter actually authorised.
 */
export function Exporters() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const query = useQuery({
    queryKey: ['exporters', q],
    queryFn: () => api.get<ExporterRow[]>('/api/console/configuration/exporters', q ? { q } : undefined),
  });

  const rows = query.data ?? [];
  const needing = rows.filter((r) => r.active && r.needsSpecimen);
  const expiring = rows.filter((r) => r.expiredSignatories > 0);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Exporters"
        lede="Every exporter registered with your chamber, and the specimen signatures of the people authorised to sign their export declarations."
      />

      <SearchInput
        value={q}
        onChange={setQ}
        placeholder="Search by company name, trading name or ABN…"
        aria-label="Search exporters"
      />

      {needing.length > 0 && (
        <Callout
          tone="warn"
          icon={<AlertTriangle className="size-4" />}
          title={`${needing.length} exporter${needing.length === 1 ? ' has' : 's have'} no current specimen on file`}
        >
          {needing.slice(0, 4).map((r) => r.companyName).join(', ')}
          {needing.length > 4 && ` and ${needing.length - 4} more`}. An operator reviewing their
          declaration has nothing to check the signature against.
        </Callout>
      )}

      {expiring.length > 0 && (
        <Callout
          tone="warn"
          icon={<RefreshCw className="size-4" />}
          title={`${expiring.length} exporter${expiring.length === 1 ? ' has' : 's have'} an expired specimen`}
        >
          A lapsed specimen stays on file and stays visible — it simply stops counting as
          current. Ask for a fresh sample, or extend it if you have re-confirmed by other means.
        </Callout>
      )}

      {query.isLoading ? (
        <Skeleton className="h-72" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Building2 className="size-5" />}
          title={q ? 'No exporter matches that' : 'No exporters yet'}
          description={
            q
              ? 'Try the ABN, or part of the trading name.'
              : 'An exporter appears here once their registration is approved.'
          }
        />
      ) : (
        <TableShell>
          <Table className="min-w-[820px]">
            <Thead>
              <Tr>
                <Th>Exporter</Th>
                <Th>ABN</Th>
                <Th numeric>Signatories</Th>
                <Th>Specimens</Th>
                <Th numeric>Certificates</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id} onClick={() => navigate(`/exporters/${row.id}`)}>
                  <Td>
                    <span className="font-medium">{row.companyName}</span>
                    {!row.active && (
                      <Badge tone="neutral" size="sm" className="ml-2">
                        Inactive
                      </Badge>
                    )}
                    {row.tradingName && row.tradingName !== row.companyName && (
                      <span className="mt-0.5 block truncate [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                        trading as {row.tradingName}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <span className="pv-num [font-size:12.5px]">{row.abn}</span>
                  </Td>
                  <Td numeric>{row.signatoryCount || <span className="text-[var(--pv-faint)]">—</span>}</Td>
                  <Td>
                    {row.currentSignatories > 0 ? (
                      <Badge tone="ok" size="sm" dot>
                        {row.currentSignatories} current
                      </Badge>
                    ) : (
                      <Badge tone="warn" size="sm" dot>None current</Badge>
                    )}
                    {row.expiredSignatories > 0 && (
                      <Badge tone="stop" size="sm" className="ml-1.5">
                        {row.expiredSignatories} expired
                      </Badge>
                    )}
                  </Td>
                  <Td numeric>{row.certificatesIssued}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}
    </div>
  );
}

export function ExporterDetail() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const { user } = useSession();
  const isAdmin = user?.role === UserRole.CHAMBER_ADMIN;
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Signatory | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);
  const [changingActive, setChangingActive] = useState(false);
  const [editingAccount, setEditingAccount] = useState<ExporterAccount | null>(null);

  const query = useQuery({
    queryKey: ['exporter', organisationId],
    queryFn: () => api.get<ExporterDetailData>(`/api/console/configuration/exporters/${organisationId}`),
    enabled: Boolean(organisationId),
  });

  if (query.isLoading) return <Skeleton className="h-96" />;
  if (!query.data) {
    return (
      <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Exporter not found">
        <Link to="/exporters" className="underline">Back to exporters</Link>
      </Callout>
    );
  }

  const data = query.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <Link
            to="/exporters"
            className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)] hover:text-[var(--pv-ink)]"
          >
            <ArrowLeft aria-hidden className="size-3.5" /> All exporters
          </Link>
          <h1 className="mt-2 [font-size:var(--pv-text-h1)] font-bold tracking-[-0.02em]">
            {data.companyName}
          </h1>
          <p className="mt-1 [font-size:13.5px] text-[var(--pv-muted)]">
            ABN <span className="pv-num">{data.abn}</span> · {data.contactName} · {data.contactEmail}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" leadingIcon={<Pencil className="size-3.5" />} onClick={() => setEditingDetails(true)}>
              Edit details
            </Button>
            <Button variant="secondary" leadingIcon={<Power className="size-3.5" />} onClick={() => setChangingActive(true)}>
              {data.active ? 'Make inactive' : 'Reactivate'}
            </Button>
            <Button variant="primary" leadingIcon={<Plus className="size-3.5" />} onClick={() => setAdding(true)}>
              Add a signatory
            </Button>
          </div>
        )}
      </div>

      {!data.active && (
        <Callout tone="warn" icon={<AlertTriangle className="size-4" />} title={`${data.companyName} is inactive`}>
          No certificate can be issued for them until they are reactivated. Their requests still arrive
          and wait in Pending, and every certificate already issued stays on record and verifiable.
          {isAdmin ? ' Use Reactivate above when they may trade again.' : ' Ask a chamber admin to reactivate them.'}
        </Callout>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Registered details" />
          <dl className="grid gap-1.5 [font-size:13px]">
            <DetailRow label="Company">{data.companyName}</DetailRow>
            {data.tradingName && <DetailRow label="Trading as">{data.tradingName}</DetailRow>}
            <DetailRow label="ABN"><span className="pv-num">{data.abn}</span></DetailRow>
            <DetailRow label="Address">
              {[data.addressLine1, data.addressLine2, `${data.suburb} ${data.state} ${data.postcode}`].filter(Boolean).join(', ')}
            </DetailRow>
            <DetailRow label="Contact">{data.contactName}</DetailRow>
            <DetailRow label="Contact email">{data.contactEmail}</DetailRow>
            {data.contactPhone && <DetailRow label="Phone">{data.contactPhone}</DetailRow>}
            <DetailRow label="Certificate requests">{data.documentCount}</DetailRow>
          </dl>
        </Card>

        <Card>
          <CardHeader
            title="Login accounts"
            hint={data.users.length ? `${data.users.length}` : undefined}
            description="The exporter's own sign-ins to the portal. Separate from signatories, whose signatures appear on declarations."
          />
          {data.users.length === 0 ? (
            <p className="[font-size:13px] text-[var(--pv-muted)]">No login accounts.</p>
          ) : (
            <ul className="flex list-none flex-col gap-0 p-0">
              {data.users.map((u) => (
                <li key={u.id} className="flex items-center gap-3 border-b border-[var(--pv-line)] py-2 last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{u.firstName} {u.lastName}</p>
                    <p className="truncate [font-size:12.5px] text-[var(--pv-faint)]">{u.email}</p>
                  </div>
                  {!u.active ? (
                    <Badge tone="neutral" size="sm">Deactivated</Badge>
                  ) : u.hasPassword ? (
                    <Badge tone="ok" size="sm" dot>Active</Badge>
                  ) : (
                    <Badge tone="warn" size="sm" dot>Invited</Badge>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="ghost" leadingIcon={<Pencil className="size-3.5" />} onClick={() => setEditingAccount(u)} aria-label={`Edit the account ${u.email}`}>
                      Edit
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {editingDetails && <EditExporterDialog exporter={data} onClose={() => setEditingDetails(false)} />}
      {changingActive && <ExporterActiveDialog exporter={data} onClose={() => setChangingActive(false)} />}
      {editingAccount && <ExporterAccountDialog organisationId={data.id} account={editingAccount} onClose={() => setEditingAccount(null)} />}

      <Card>
        <CardHeader
          title="Authorised signatories"
          hint={data.signatories.length ? `${data.signatories.length}` : undefined}
          description={`Specimens are accepted for ${data.validityMonths} months at this chamber. A lapsed specimen stays on file — it simply stops counting as current.`}
        />

        {data.signatories.length === 0 ? (
          <EmptyState
            icon={<PenLine className="size-5" />}
            title="No signatories recorded"
            description="Record the people authorised to sign this exporter's declarations, and the specimen of each signature."
            action={
              isAdmin && (
                <Button variant="primary" onClick={() => setAdding(true)}>
                  Add the first signatory
                </Button>
              )
            }
          />
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {data.signatories.map((sig) => (
              <div
                key={sig.id}
                className={
                  'flex items-start gap-3 rounded-[var(--pv-radius)] border p-3 ' +
                  (!sig.active ? 'opacity-60 ' : '') +
                  (sig.active && (sig.status === 'EXPIRED' || sig.status === 'NO_SPECIMEN')
                    ? 'border-[var(--pv-warn-line)] bg-[var(--pv-warn-bg)]/40'
                    : 'border-[var(--pv-line)]')
                }
              >
                <div className="grid h-16 w-28 shrink-0 place-items-center rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-white p-1.5">
                  {sig.specimenUrl ? (
                    <img
                      src={sig.specimenUrl}
                      alt={`Specimen signature of ${sig.fullName}`}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <span className="[font-size:11px] text-[var(--pv-faint)]">No specimen</span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{sig.fullName}</p>
                  <p className="truncate [font-size:12.5px] text-[var(--pv-muted)]">{sig.position}</p>
                  {sig.email && (
                    <p className="truncate [font-size:12px] text-[var(--pv-faint)]">{sig.email}</p>
                  )}

                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <Badge tone={STATUS[sig.status].tone} size="sm" dot>
                      {STATUS[sig.status].label}
                    </Badge>
                    {sig.validUntil && (
                      <Tooltip
                        content={`Accepted ${new Date(sig.validFrom).toLocaleDateString('en-AU')}`}
                      >
                        <span className="pv-num [font-size:11.5px] text-[var(--pv-faint)]">
                          until {new Date(sig.validUntil).toLocaleDateString('en-AU')}
                          {sig.daysUntilExpiry !== null &&
                            sig.daysUntilExpiry > 0 &&
                            sig.daysUntilExpiry < 90 &&
                            ` · ${sig.daysUntilExpiry}d`}
                        </span>
                      </Tooltip>
                    )}
                  </div>

                  {isAdmin && (
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={<Pencil className="size-3.5" />}
                        onClick={() => setEditing(sig)}
                        aria-label={`Edit ${sig.fullName}`}
                      >
                        {sig.specimenUrl ? 'Edit' : 'Edit · add specimen'}
                      </Button>
                      {sig.specimenUrl && sig.active && <ExtendButton signatoryId={sig.id} name={sig.fullName} />}
                      <SignatoryActiveButton signatory={sig} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {adding && organisationId && (
        <SignatoryDialog organisationId={organisationId} onClose={() => setAdding(false)} />
      )}
      {editing && organisationId && (
        <SignatoryDialog
          organisationId={organisationId}
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/**
 * Extends the window on the specimen already held, without a new sample.
 *
 * For a chamber that has re-confirmed by other means — a phone call, a signed letter. The
 * server audits it separately from a renewal with a new image, because they are different
 * assurances and a reviewer should be able to tell them apart.
 */
function ExtendButton({ signatoryId, name }: { signatoryId: string; name: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const extend = useMutation({
    mutationFn: () => api.post(`/api/console/configuration/signatories/${signatoryId}/extend`, {}),
    onSuccess: () => {
      toast.success(`Extended ${name}`, 'The specimen already on file is valid again.');
      void queryClient.invalidateQueries({ queryKey: ['exporter'] });
      void queryClient.invalidateQueries({ queryKey: ['exporters'] });
    },
    onError: (error) =>
      toast.error('Could not extend it', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Tooltip content="Keep the specimen on file and restart its validity period">
      <span>
        <Button variant="ghost" size="sm" loading={extend.isPending} onClick={() => extend.mutate()}>
          Extend
        </Button>
      </span>
    </Tooltip>
  );
}

function SignatoryDialog({
  organisationId,
  existing,
  onClose,
}: {
  organisationId: string;
  existing?: Signatory;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState(existing?.fullName ?? '');
  const [position, setPosition] = useState(existing?.position ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [file, setFile] = useState<File | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        fullName,
        position,
        email: email || null,
        ...(file ? { filename: file.name, contentBase64: await toBase64(file) } : {}),
      };
      return existing
        ? api.patch(`/api/console/configuration/signatories/${existing.id}`, body)
        : api.post(`/api/console/configuration/exporters/${organisationId}/signatories`, body);
    },
    onSuccess: () => {
      toast.success(
        existing ? 'Signatory updated' : `${fullName} recorded`,
        file ? 'The validity period starts from today.' : undefined,
      );
      void queryClient.invalidateQueries({ queryKey: ['exporter'] });
      void queryClient.invalidateQueries({ queryKey: ['exporters'] });
      onClose();
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  const previewUrl = file ? URL.createObjectURL(file) : existing?.specimenUrl;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={existing ? `Edit ${existing.fullName}` : 'Add a signatory'}
          description={
            existing
              ? 'Change their position or email, or upload a new specimen. A new specimen restarts the validity period — that is what re-verification means.'
              : "Someone authorised to sign this exporter's declarations, and a specimen of their signature."
          }
        />
        <DialogBody>
          <div className="flex flex-col gap-3.5">
            <Field
              label="Full name"
              required
              help={existing ? 'Fixed: a specimen belongs to one person. For someone else, add a new signatory.' : undefined}
            >
              <Input
                value={fullName}
                disabled={Boolean(existing)}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Margaret Chen"
              />
            </Field>

            <Field label="Position" required help="Their role in the export operation.">
              <Input
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder="Export Manager"
              />
            </Field>

            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>

            <Field
              label={existing?.specimenUrl ? 'Replace specimen (optional)' : 'Specimen signature'}
              help={
                existing?.specimenUrl
                  ? 'Leave empty to keep the current one. PNG or JPEG.'
                  : 'A scan of their handwritten signature. PNG or JPEG.'
              }
            >
              <div className="flex items-center gap-3">
                <div className="grid h-16 w-28 shrink-0 place-items-center rounded-[var(--pv-radius-sm)] border border-dashed border-[var(--pv-line)] bg-white p-1.5">
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <Upload aria-hidden className="size-5 text-[var(--pv-faint)]" />
                  )}
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="[font-size:13px] file:mr-3 file:rounded-[var(--pv-radius-sm)] file:border file:border-[var(--pv-line)] file:bg-[var(--pv-paper)] file:px-3 file:py-1.5 file:[font-size:13px]"
                  aria-label="Choose a specimen signature image"
                />
              </div>
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!fullName.trim() || !position.trim()}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {existing ? 'Save' : 'Add signatory'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[9rem_minmax(0,1fr)] gap-2">
      <dt className="text-[var(--pv-faint)]">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

function useExporterMutation(organisationId: string, onDone: () => void) {
  const toast = useToast();
  const queryClient = useQueryClient();
  return {
    onSuccess: (title: string, detail?: string) => {
      toast.success(title, detail);
      void queryClient.invalidateQueries({ queryKey: ['exporter', organisationId] });
      void queryClient.invalidateQueries({ queryKey: ['exporters'] });
      onDone();
    },
    onError: (error: unknown) => toast.error('Could not save', error instanceof ApiError ? error.message : 'Something went wrong.'),
  };
}

function EditExporterDialog({ exporter, onClose }: { exporter: ExporterDetailData; onClose: () => void }) {
  const [form, setForm] = useState({
    companyName: exporter.companyName,
    tradingName: exporter.tradingName ?? '',
    abn: exporter.abn,
    addressLine1: exporter.addressLine1,
    addressLine2: exporter.addressLine2 ?? '',
    suburb: exporter.suburb,
    state: exporter.state,
    postcode: exporter.postcode,
    contactName: exporter.contactName,
    contactEmail: exporter.contactEmail,
    contactPhone: exporter.contactPhone ?? '',
  });
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });
  const handlers = useExporterMutation(exporter.id, onClose);
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/console/configuration/exporters/${exporter.id}`, {
        ...form,
        tradingName: form.tradingName.trim() || null,
        addressLine2: form.addressLine2.trim() || null,
        contactPhone: form.contactPhone.trim() || null,
      }),
    onSuccess: () => handlers.onSuccess('Details saved'),
    onError: handlers.onError,
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[40rem]">
        <DialogHeader title={`Edit ${exporter.companyName}`} description="Recorded in the audit trail, before and after. Certificates already issued keep the details they were issued with." />
        <DialogBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Company name" required className="sm:col-span-2"><Input value={form.companyName} onChange={set('companyName')} /></Field>
            <Field label="Trading name"><Input value={form.tradingName} onChange={set('tradingName')} /></Field>
            <Field label="ABN" required><Input value={form.abn} onChange={set('abn')} className="pv-num" /></Field>
            <Field label="Address" required className="sm:col-span-2"><Input value={form.addressLine1} onChange={set('addressLine1')} /></Field>
            <Field label="Address line 2" className="sm:col-span-2"><Input value={form.addressLine2} onChange={set('addressLine2')} /></Field>
            <Field label="Suburb" required><Input value={form.suburb} onChange={set('suburb')} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="State" required><Input value={form.state} onChange={set('state')} /></Field>
              <Field label="Postcode" required><Input value={form.postcode} onChange={set('postcode')} className="pv-num" /></Field>
            </div>
            <Field label="Contact name" required><Input value={form.contactName} onChange={set('contactName')} /></Field>
            <Field label="Contact phone"><Input value={form.contactPhone} onChange={set('contactPhone')} /></Field>
            <Field label="Contact email" required className="sm:col-span-2" help="Where outcome emails go. Not a login — accounts are edited separately.">
              <Input type="email" value={form.contactEmail} onChange={set('contactEmail')} />
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExporterActiveDialog({ exporter, onClose }: { exporter: ExporterDetailData; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const handlers = useExporterMutation(exporter.id, onClose);
  const activate = !exporter.active;
  const save = useMutation({
    mutationFn: () => api.post(`/api/console/configuration/exporters/${exporter.id}/active`, { active: activate, reason }),
    onSuccess: () =>
      handlers.onSuccess(
        activate ? 'Reactivated' : 'Made inactive',
        activate ? 'Certificates can be issued for them again.' : 'No certificate can be issued for them until reactivated.',
      ),
    onError: handlers.onError,
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={activate ? `Reactivate ${exporter.companyName}?` : `Make ${exporter.companyName} inactive?`}
          description={
            activate
              ? 'Their waiting requests become approvable again.'
              : `Exporters are never deleted: their ${exporter.documentCount} request${exporter.documentCount === 1 ? '' : 's'} and every issued certificate stay on record and verifiable. While inactive, nothing new can be issued for them; their requests still arrive and wait.`
          }
        />
        <DialogBody>
          <Field label="Reason" required help="Kept in the audit trail.">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={activate ? 'Membership renewed' : 'Membership lapsed'} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant={activate ? 'primary' : 'danger'} disabled={reason.trim().length < 3} loading={save.isPending} onClick={() => save.mutate()}>
            {activate ? 'Reactivate' : 'Make inactive'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExporterAccountDialog({ organisationId, account, onClose }: { organisationId: string; account: ExporterAccount; onClose: () => void }) {
  const [firstName, setFirstName] = useState(account.firstName);
  const [lastName, setLastName] = useState(account.lastName);
  const [email, setEmail] = useState(account.email);
  const handlers = useExporterMutation(organisationId, onClose);
  const save = useMutation({
    mutationFn: (extra: { active?: boolean }) =>
      api.patch(`/api/console/configuration/exporters/${organisationId}/users/${account.id}`, {
        firstName,
        lastName,
        ...(email.trim().toLowerCase() !== account.email ? { email: email.trim() } : {}),
        ...extra,
      }),
    onSuccess: (_r, extra) =>
      handlers.onSuccess(extra.active === false ? 'Account deactivated' : extra.active ? 'Account reactivated' : 'Account saved'),
    onError: handlers.onError,
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={`Edit ${account.firstName} ${account.lastName}`}
          description="Each email address can have one Provenir account. To free an address for another use, change it here first."
        />
        <DialogBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="First name" required><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Field>
            <Field label="Last name" required><Input value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
            <Field label="Sign-in email" required className="sm:col-span-2"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            className="mr-auto"
            loading={save.isPending}
            onClick={() => save.mutate({ active: !account.active })}
          >
            {account.active ? 'Deactivate account' : 'Reactivate account'}
          </Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!email.trim() || !firstName.trim() || !lastName.trim()} loading={save.isPending} onClick={() => save.mutate({})}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Switch a signatory off, or back on. Never deleted: the specimen stays on file against
 * every certificate it was checked for. While inactive they cannot upload certificates or
 * send them by email, and their specimen no longer counts as current.
 */
function SignatoryActiveButton({ signatory }: { signatory: Signatory }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const activate = !signatory.active;
  const save = useMutation({
    mutationFn: () => api.patch(`/api/console/configuration/signatories/${signatory.id}`, { active: activate }),
    onSuccess: () => {
      toast.success(activate ? `${signatory.fullName} reactivated` : `${signatory.fullName} deactivated`);
      void queryClient.invalidateQueries({ queryKey: ['exporter'] });
      void queryClient.invalidateQueries({ queryKey: ['exporters'] });
      setConfirming(false);
    },
    onError: (error) => toast.error('Could not change the signatory', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });
  return (
    <>
      <Button variant="ghost" size="sm" leadingIcon={<Power className="size-3.5" />} onClick={() => setConfirming(true)}>
        {activate ? 'Reactivate' : 'Deactivate'}
      </Button>
      {confirming && (
        <Dialog open onOpenChange={(open) => !open && setConfirming(false)}>
          <DialogContent>
            <DialogHeader
              title={activate ? `Reactivate ${signatory.fullName}?` : `Deactivate ${signatory.fullName}?`}
              description={
                activate
                  ? 'They can submit certificates again, and their specimen counts once more if it is still within its validity period.'
                  : 'For someone who has left the exporter or is no longer authorised to sign. They can no longer upload or email certificates, and their specimen stops counting as current. Nothing is deleted: their specimen stays on file against every certificate it was checked for.'
              }
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
              <Button variant={activate ? 'primary' : 'danger'} loading={save.isPending} onClick={() => save.mutate()}>
                {activate ? 'Reactivate' : 'Deactivate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
